import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import CustomDatePicker from "common/CustomDatePicker";
import CustomSelect from "components/Common/custom-select";
import TableContainer from "components/Common/TableContainer";
import { useFormik } from "formik";
import * as Yup from "yup";
import Loader from "common/pageLoader";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { fetchEmployeeDropdownAutoList, fetchExpenseEntryReport } from "store/actions";
import { exportEmployeeToExcel } from "pages/exports/exportExcel";
import { exportEmployeeToPDF } from "pages/exports/exportPDF";
import toast from "react-hot-toast";

const ExpenseReport = () => {
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleDropdown = () => setDropdownOpen((prev) => !prev);
  const {
    dropdownAutoList,
    reports,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    dropdownAutoList: state.EmployeeDetailsReducer?.dropdownAutoList ?? [],
    reports: state.ExpensesMasterReducer?.reports ?? [],

    loading: state.ExpensesMasterReducer?.loading || state.EmployeeDetailsReducer?.loading,
    error: state.ExpensesMasterReducer?.error || state.EmployeeDetailsReducer?.error,
    success: state.ExpensesMasterReducer?.success,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchEmployeeDropdownAutoList(unitId));
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      employee: "",
      fromDate: null,
      toDate: null,
      department: null,
      role: null,
      head: null,
      subHead: null,
    },
    validationSchema: Yup.object({
      // employee: Yup.object().nullable().required("Employee is required"),

      fromDate: Yup.date()
        .nullable()
        .required("From Date is required"),

      toDate: Yup.date()
        .nullable()
        .required("To Date is required")
        .min(Yup.ref("fromDate"), "To Date must be the same or after From Date"),
    }),
    onSubmit: (values) => {
      console.log("Search submitted:", values);
      const formData = new FormData();
      formData.append("emp_id", values.employee?.value?.toString() || 0);
      formData.append("from_date", values.fromDate || null);
      formData.append("to_date", values.toDate || null);
      formData.append("unit_id", authDetails?.unit_id);
      formData.append("create_by", authDetails?.user_id);
      dispatch(fetchExpenseEntryReport(formData))
    },
  });

  const columns = [
    { Header: "S.No", Cell: ({ row }) => reports.length - row.index, className: "serial-no text-center", applyHeaderClass: true },
    // { Header: "Emp No", accessor: "emp_no" },
    { Header: "Name", accessor: "emp_name" },
    { Header: "Date", accessor: "exp_date" },
    { Header: "Department", accessor: "dep_name" },
    { Header: "Role", accessor: "designation" },
    { Header: "Head", accessor: "head_name" },
    { Header: "Subhead", accessor: "subhead_name" },
    { Header: "Amount", accessor: "amount" },
    { Header: "Status", accessor: "exp_status" },
  ];

  const handleReset = () => {
    formik.resetForm();
  };

  const handleExportExcel = () => {
    if (!Array.isArray(reports) || reports.length === 0) {
      toast.error("No data to export.");
      return;
    }
    exportEmployeeToExcel(reports, authDetails?.unit_fullname || "Company", { address: authDetails?.unit_address || "" }, "Expense_Report");
  };

  const handleExportPDF = () => {
    if (!Array.isArray(reports) || reports.length === 0) {
      toast.error("No data to export.");
      return;
    }
    exportEmployeeToPDF(reports, authDetails?.unit_fullname || "Company", { address: authDetails?.unit_address || "" }, "Expense_Report", columns);
  };

  return (
    <div className="page-content">
      <Loader loading={loading}/>
      <Container fluid>
        <Breadcrumbs title="Expenses" breadcrumbItem="Expense Report" />

        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit}>
              <Row className="mb-3">
                <Col md={3}>
                  <FormGroup>
                    <Label for="employee">Employee</Label>
                    <CustomSelect
                      id="employee"
                      name="employee"
                      options={(dropdownAutoList || []).map(bg => ({ value: bg.emp_id.toString(), label: bg.emp_no }))}
                      value={formik.values.employee || null}
                      onChange={(selectedOption) => formik.setFieldValue("employee", selectedOption)}
                      onBlur={formik.handleBlur}
                      className={formik.touched.employee && formik.errors.employee ? "is-invalid" : ""}
                    />
                    {formik.touched.employee && formik.errors.employee && (
                      <div className="text-danger">{formik.errors.employee}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>From <span className="text-danger">*</span></Label>
                    <CustomDatePicker
                      name="fromDate"
                      value={formik.values.fromDate}
                      onChange={(date) => formik.setFieldValue("fromDate", date)}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.fromDate && formik.errors.fromDate && (
                      <div className="text-danger">{formik.errors.fromDate}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>To <span className="text-danger">*</span></Label>
                    <CustomDatePicker
                      name="toDate"
                      value={formik.values.toDate}
                      onChange={(date) => formik.setFieldValue("toDate", date)}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.toDate && formik.errors.toDate && (
                      <div className="text-danger">{formik.errors.toDate}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col className="d-flex align-items-center justify-content-end">
                  <Button type="submit" className="btn button-search me-2">
                    <i className="fas fa-search me-1" /> Search
                  </Button>
                  <Button type="button" onClick={handleReset} className="btn button-cancel me-2">
                    <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                  <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} direction="down">
                    <DropdownToggle color="primary" className="btn button-export">
                      <i className="fas fa-file-export me-1" /> Export
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem onClick={handleExportExcel}>
                        <i className="fas fa-file-excel me-2 text-success" /> Export to Excel
                      </DropdownItem>
                      <DropdownItem onClick={handleExportPDF}>
                        <i className="fas fa-file-pdf me-2 text-danger" /> Export to PDF
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <TableContainer
              columns={columns}
              data={reports}
              isGlobalFilter={true}
              customPageSize={10}
              tableSorter={true}
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ExpenseReport;
